import { DataManager } from 'utils/DataManager/dataManager';
import { IManager } from 'typings/manager';
import { IWorkflowStatusManager } from 'typings/manager';
import { IManagerListTypeSummaryLookup } from 'typings/manager';
import { DataState, WorkflowStatus, SidePanelListType } from 'typings/enums';
import { IManagerDataManager } from 'typings/dataManagers';
import { ICountryBudgetDecision } from 'typings/countryBudgetDecision';
import { ICountryEmployeeDecision } from 'typings/countryEmployeeDecision';
import { ISetManagerWorkflowEvent } from 'typings/workflow';
import { IManagerWorkflow } from 'typings/manager';
import { IEmployee } from 'typings/employee';
import { ChangeHistoryContainer } from 'nomst/Stores/ChangeHistory/changeHistoryContainer';

class ManagerDataManager extends DataManager implements IManagerDataManager {
    private readonly defaultCsvFilename: string = 'Merit-Export.csv';

    async getManagers(
        masterAccountId: string,
        cycleId: number,
        managerIds: string[]): Promise<Nullable<IManager[]>> {
        const receive = () => this._postDataRequest<string[], IManager[]>(
            // tslint:disable-next-line:max-line-length
            `/api/accounts/${masterAccountId}/team/cycles/${cycleId}/mobx/managers`,
            managerIds
        );

        return this.getData<IManager[]>(receive, false);
    }

    async getFirstManagerByListAsync(
        masterAccountId: string,
        cycleId: number,
        sidePanelListType: SidePanelListType,
        subType: string): Promise<Nullable<IManager>> {
        const receive = () => this._getDataRequest<IManager[]>(
            // tslint:disable-next-line:max-line-length
            `/api/accounts/${masterAccountId}/team/cycles/${cycleId}/mobx/list/${sidePanelListType}/listTypes/${subType}/managers?topCount=1`
        );

        const managers = await this.getData<IManager[]>(receive, false);
        return managers ? managers[0] : null;
    }

    getManagerListTypeSummaryLookups(
        masterAccountId: string,
        cycleId: number,
        sidePanelListType: SidePanelListType): Promise<Nullable<IManagerListTypeSummaryLookup[]>> {
        const receive = () => this._getDataRequest<IManager[]>(
            `/api/accounts/${masterAccountId}/team/cycles/${cycleId}/mobx/list/${sidePanelListType}/subTypes/summaries`
        );

        const dataOptions = {
            getDataFunc: receive,
            blockUi: false,
            redirectOnError: true
        };
        return this.getDataWithOptions<IManagerListTypeSummaryLookup[]>(dataOptions);
    }

    async getManagerWorkflow(
        masterAccountId: string,
        cycleId: number,
        managerSourceId: string): Promise<Nullable<IManagerWorkflow>> {
        const receive = () => this._getDataRequest<IManagerWorkflow>(
            // tslint:disable-next-line:max-line-length
            `/api/accounts/${masterAccountId}/team/cycles/${cycleId}/managers/workflow?managerSourceId=${encodeURIComponent(managerSourceId)}`
        );
        return this.getData<IManagerWorkflow>(receive, false);
    }

    async getManagerWorkflows(
        masterAccountId: string,
        cycleId: number,
        managerSourceIds: string[]): Promise<Nullable<IManagerWorkflow[]>> {
        const receive = () => this._postDataRequest<string[], IManagerWorkflow[]>(
            `/api/accounts/${masterAccountId}/team/cycles/${cycleId}/managers/multiWorkflow`,
            managerSourceIds
        );
        return this.getData<IManagerWorkflow[]>(receive, false);
    }

    async getWorkflowStatusManagers(
        masterAccountId: string,
        cycleId: number,
        workflowStatus: WorkflowStatus): Promise<Nullable<IWorkflowStatusManager[]>> {
        const receive = () => this._getDataRequest<IWorkflowStatusManager[]>(
            // tslint:disable-next-line:max-line-length
            `/api/accounts/${masterAccountId}/team/cycles/${cycleId}/summaries/managers/workflowstatus?workflowStatus=${workflowStatus}`
        );
        return this.getData<IWorkflowStatusManager[]>(receive, false);
    }

    async getChangeHistoryContainers(
        masterAccountId: string,
        cycleId: number,
        managerSourceId: string
    ): Promise<Nullable<ChangeHistoryContainer[]>> {
        const receive = () => this._getDataRequest<ChangeHistoryContainer[]>(
            // tslint:disable-next-line:max-line-length
            `/api/accounts/${masterAccountId}/team/cycles/${cycleId}/managers/changehistory?managerSourceId=${encodeURIComponent(managerSourceId)}`
        );
        return this.getData<ChangeHistoryContainer[]>(receive);
    }

    async getOverBudgetManagers(
        masterAccountId: string,
        cycleId: number,
        budgetCycleId: number): Promise<Nullable<IManagerCardOverBudget[]>> {
        const receive = () => this._getDataRequest<IManagerCardOverBudget[]>(
            // tslint:disable-next-line:max-line-length
            `/api/accounts/${masterAccountId}/team/cycles/${cycleId}/summaries/managers/overbudget?budgetCycleId=${budgetCycleId}`
        );
        return this.getData<IManagerCardOverBudget[]>(receive, false);
    }

    async applyBudgets(
        masterAccountId: string,
        cycleId: number,
        budgetCycleId: number,
        managerBudgets: IManagerBudget[],
        countryCode?: string): Promise<void> {
        const option = countryCode && countryCode.length > 0 ? `?countryCode=${countryCode}` : '';
        const receive = () => this._putDataRequest<IManagerBudget[], void>(
            `/api/accounts/${masterAccountId}/team/cycles/${cycleId}/budgets/${budgetCycleId}${option}`,
            managerBudgets
        );

        await this.getData<void>(receive, false);
    }
    async setManagerWorkflow(
        masterAccountId: string,
        cycleId: number,
        events: ISetManagerWorkflowEvent[]
    ): Promise<void> {
        const receive = () => this._postDataRequest<typeof events, void>(
            // tslint:disable-next-line:max-line-length
            `/api/accounts/${masterAccountId}/team/cycles/${cycleId}/managers/workflow`, events);

        await this.getData<void>(receive, true);
    }

    async getCountryBudgetDecisions(
        masterAccountId: string,
        cycleId: number,
        managerIds: string[],
        isDirectOnly: boolean,
        orderBy: string,
        employeesPerPage: number,
        promotionOnly?: boolean
    ): Promise<Nullable<ICountryBudgetDecision[]>> {
        managerIds.sort();
        const employeesPerPageString = `&employeesPerPage=${employeesPerPage}`;
        const option = isDirectOnly ? `?isDirectOnly=true&$orderBy=${orderBy}` :
            `?$orderBy=${orderBy}`;
        const promo = promotionOnly ? `&promotionOnly=${promotionOnly}` : '';
        const query = `${option}${employeesPerPageString}${promo}`;

        const receive = () => this._postDataRequest<string[], ICountryBudgetDecision[]>(
            `/api/accounts/${masterAccountId}/team/cycles/${cycleId}/managers/multimanager/employees${query}`,
            managerIds);

        const dataOptions = {
            getDataFunc: receive,
            blockUi: true,
            redirectOnError: true
        };
        return await this.getDataWithOptions<ICountryBudgetDecision[]>(dataOptions);
    }

    async getPagedEmployees(
        masterAccountId: string,
        cycleId: number,
        managerIds: string[],
        isDirectOnly: boolean,
        orderBy: string,
        employeesPerPage: number,
        pageNumber: number,
        country: string,
        numberOfPages: number,
        promotionOnly?: boolean,
        jobTitle?: string,
        department?: string
    ): Promise<Nullable<IEmployee[]>> {
        managerIds.sort();
        const employeesPerPageString = `&employeesPerPage=${employeesPerPage}`;
        const pageNumberString = `&pageNumber=${pageNumber}`;
        const countryString = `&country=${country}`;
        const numberOfPagesString = `&numberOfPages=${numberOfPages}`;
        const option = isDirectOnly ? `?isDirectOnly=true&$orderBy=${orderBy}` :
            `?$orderBy=${orderBy}`;
        const promo = promotionOnly ? `&promotionOnly=${promotionOnly}` : '';
        const jobTitleString = jobTitle ? `&jobTitle=${jobTitle}` : '';
        const departmentString = department ? `&department=${department}` : '';
        const query = `${option}${employeesPerPageString}${pageNumberString}${countryString}${numberOfPagesString}${promo}${jobTitleString}${departmentString}`;

        const receive = () => this._postDataRequest<string[], IEmployee[]>(
            `/api/accounts/${masterAccountId}/team/cycles/${cycleId}/managers/multimanager/paged/employees${query}`,
            managerIds);
        
        const dataOptions = {
            getDataFunc: receive,
            blockUi: true,
            redirectOnError: true
        };
        return await this.getDataWithOptions<IEmployee[]>(dataOptions);

    }

    async getCountryEmployeeDecisions(
        masterAccountId: string,
        cycleId: number,
        orderBy: string,
        baseRangeId: number): Promise<Nullable<ICountryEmployeeDecision[]>> {
        const receive = () => this._getDataRequest<ICountryEmployeeDecision[]>(
            // tslint:disable-next-line:max-line-length
            `/api/accounts/${masterAccountId}/team/cycles/${cycleId}/managers/multimanager/payrangeemployees?rangeId=${baseRangeId}`);
        const dataOptions = {
            getDataFunc: receive,
            blockUi: true,
            redirectOnError: true
        };
        return await this.getDataWithOptions<ICountryEmployeeDecision[]>(dataOptions);
    }

    getManagerBudgets(
        masterAccountId: string,
        cycleId: number,
        budgetCycleId: number,
        countryCode?: string
    ): Promise<Nullable<IManagerBudget[]>> {
        const option = countryCode && countryCode.length > 0 ? `?countryCode=${countryCode}` : '';
        const receive = () => this._getDataRequest<IManagerBudget[]>(
            `/api/accounts/${masterAccountId}/team/cycles/${cycleId}/budgets/${budgetCycleId}${option}`
        );
        return this.getData<IManagerBudget[]>(receive, false);
    }

    pollManagerBudgets(
        masterAccountId: string,
        cycleId: number,
        budgetCycleId: number): Promise<Nullable<IManagerBudget[]>> {
        const receive = () => this._getDataRequest<IManagerBudget[]>(
            `/api/accounts/${masterAccountId}/team/cycles/${cycleId}/budgets/${budgetCycleId}/cached`
        );
        return this.getData<IManagerBudget[]>(receive, false);
    }

    async exportReportsToCsv(
        masterAccountId: string,
        cycleId: number,
        managerSourceIds: string[],
        directReportOnly: boolean
    ): Promise<void> {
        const option = `?isDirectOnly=${!!directReportOnly}`;
        const receive = () => this._downloadDataRequest(
            `/api/accounts/${masterAccountId}/team/cycles/${cycleId}/managers/employees/csv${option}`,
            this.defaultCsvFilename,
            'POST',
            managerSourceIds
        );
        await this.getData<void>(receive);
    }

    async exportAllManagersToCsv(
        masterAccountId: string,
        cycleId: number
    ): Promise<void> {
        const receive = () => this._downloadDataRequest(
            `/api/accounts/${masterAccountId}/team/cycles/${cycleId}/managers/csv`,
            this.defaultCsvFilename,
            'GET'
        );
        await this.getData<void>(receive);
    }

    setDataState(state: DataState): void {
        this.protectedTransitionDataState(state);
    }

    async getEmployeeCompMemoData(
        masterAccountId: string,
        cycleId: number,
        employeeSourceId: string
    ): Promise<Nullable<IEmployee>> {
       
        const receive = () => this._getDataRequest<IEmployee>(
            `/api/accounts/${masterAccountId}/team/cycles/${cycleId}/employee/compMemo?employeeSourceId=${employeeSourceId}`);
        
        const compData =  await this.getData<IEmployee>(receive, false);
        return compData ?? null;
    }
}

export { ManagerDataManager };
export default ManagerDataManager;